import { useEffect, useState, useRef } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { BranchCell } from "@/components/BranchFlag";
import { PageHeader, TableShell } from "@/components/EntityManager";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { HEAD_OFFICE } from "@/lib/company";
import { Plus, Pencil, Trash2, Printer, FileSpreadsheet, Minus, Maximize2, Minimize2, X, MessageCircle, BellRing, Phone, PhoneCall, BarChart3, Search, Upload, FileCheck2, ExternalLink } from "lucide-react";
import { inr, fmtDate, today } from "@/lib/format";
import { printBillSubmission, printBillSubmissionRegister, printCallOutcomeReport, printCollectionSummary } from "@/lib/print";
import { toast } from "sonner";

const STATUSES = ["Submitted", "Acknowledged", "Cleared", "Returned"];
const STATUS_CLR = {
  Submitted: "bg-blue-50 text-blue-700 border-blue-200",
  Acknowledged: "bg-amber-50 text-amber-700 border-amber-200",
  Cleared: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Returned: "bg-rose-50 text-rose-700 border-rose-200",
};

const OUTCOMES = ["Payment Promised", "Will Check & Revert", "No Response", "Disputed", "Partly Paid", "Cheque Ready", "Other"];
const OUTCOME_CLR = {
  "Payment Promised": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Cheque Ready": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Partly Paid": "bg-sky-50 text-sky-700 border-sky-200",
  "Will Check & Revert": "bg-amber-50 text-amber-700 border-amber-200",
  "No Response": "bg-rose-50 text-rose-700 border-rose-200",
  Disputed: "bg-rose-50 text-rose-700 border-rose-200",
};

const empty = () => ({
  date: today(),
  invoice_id: "",
  invoice_no: "",
  bill_date: "",
  cn_no: "",
  cn_date: "",
  from_place: "",
  destination: "",
  customer_id: "",
  customer_name: "",
  customer_code: "",
  customer_address: "",
  amount: 0,
  submitted_to: "",
  received_by: "",
  expected_date: "",
  status: "Submitted",
  remarks: "",
  bills: [],
});

import { BRANCHES as BSBRANCHES } from "@/lib/branches";

export default function BillSubmission() {
  const [rows, setRows] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [entryMode, setEntryMode] = useState("select");
  const [mode, setMode] = useState("single");
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [dlgView, setDlgView] = useState("normal");
  const [followupOpen, setFollowupOpen] = useState(false);
  const [callRow, setCallRow] = useState(null);
  const [callForm, setCallForm] = useState({ date: today(), outcome: "", next_call_date: "", notes: "" });
  const [callSaving, setCallSaving] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportMonth, setReportMonth] = useState(today().slice(0, 7));
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryMonth, setSummaryMonth] = useState(today().slice(0, 7));
  const [q, setQ] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [branchPick, setBranchPick] = useState("All");
  const [noBranchOnly, setNoBranchOnly] = useState(false);
  const [branchContacts, setBranchContacts] = useState({});
  const slipInputs = useRef({});
  const [slipUploadingId, setSlipUploadingId] = useState("");
  const slipUrl = (r) => `${API}/files/${r.slip_path}?auth=${localStorage.getItem("ntc_token")}`;
  const onSlipFile = async (r, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setSlipUploadingId(r.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/bill-submissions/${r.id}/slip`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Submission slip uploaded");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setSlipUploadingId("");
    }
  };
  const deleteSlip = async (r) => {
    if (!window.confirm("Remove this submission slip?")) return;
    try {
      await api.delete(`/bill-submissions/${r.id}/slip`);
      toast.success("Submission slip removed");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  useEffect(() => {
    api.get("/branch-contacts").then((r) => setBranchContacts(r.data || {})).catch(() => {});
  }, []);
  const codeByName = (() => {
    const m = {};
    customers.forEach((c) => { if (c.name && c.customer_code) m[String(c.name).trim().toLowerCase()] = c.customer_code; });
    return m;
  })();
  const custCode = (r) => r.customer_code || codeByName[String(r.customer_name || "").trim().toLowerCase()] || "";
  const branchOf = (r) => r.office || (r.bills && r.bills[0] && r.bills[0].branch) || "—";
  const isOverdueSub = (r) => r.expected_date && String(r.expected_date).slice(0, 10) < today() && !/paid|cleared|settled|received/i.test(r.status || "");
  const shown = (rows || []).filter((r) => {
    const d = String(r.date || "").slice(0, 10);
    if (fromDate && (!d || d < fromDate)) return false;
    if (toDate && (!d || d > toDate)) return false;
    if (branchPick !== "All" && branchOf(r) !== branchPick) return false;
    if (noBranchOnly && branchOf(r) !== "—") return false;
    const needle = q.trim().toLowerCase();
    if (needle) {
      const hay = [r.customer_name, custCode(r), String(r.submission_no || "").replace(/^BS[-\s]?/i, ""), String(r.invoice_no || "").replace(/^INV[-\s]?/i, ""), r.submitted_to, r.office];
      if (!hay.some((x) => String(x || "").toLowerCase().includes(needle))) return false;
    }
    return true;
  });
  const toggleRow = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const loadAll = async () => {
    try {
      const [subs, inv] = await Promise.all([api.get("/bill-submissions"), api.get("/invoices")]);
      setRows(subs.data);
      setInvoices(inv.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => {
    loadAll();
    api.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("ntc_draft_submission");
      if (raw) {
        const d = JSON.parse(raw);
        setForm(d.form || empty());
        setMode(d.mode || "single");
        setEntryMode(d.entryMode || "select");
        setEditing(d.editing || null);
        setDlgView("min");
        setOpen(true);
      }
    } catch (e) { /* ignore */ }
  }, []);

  const clearDraft = () => { try { localStorage.removeItem("ntc_draft_submission"); } catch (e) {} };
  const saveDraft = () => { try { localStorage.setItem("ntc_draft_submission", JSON.stringify({ form, mode, entryMode, editing })); } catch (e) {} };

  const openCreate = () => {
    setEditing(null);
    setEntryMode("select");
    setMode("single");
    setForm(empty());
    setDlgView("normal");
    setOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setEntryMode(row.invoice_id ? "select" : "manual");
    setMode(row.bills && row.bills.length ? "multi" : "single");
    setForm({ ...empty(), ...row, bills: row.bills || [] });
    setDlgView("normal");
    setOpen(true);
  };

  const pickInvoice = (id) => {
    const inv = invoices.find((i) => i.id === id);
    setForm((f) => ({
      ...f,
      invoice_id: id,
      invoice_no: inv?.invoice_no || "",
      bill_date: inv?.issue_date || f.bill_date,
      customer_id: inv?.customer_id || "",
      customer_name: inv?.customer_name || "",
      amount: inv?.total || 0,
    }));
  };

  const blankBill = () => ({ branch: "Chennai", bill_no: "", bill_date: "", cn_no: "", cn_date: "", from_place: "", destination: "", amount: 0 });
  const billsTotal = (bills) => (bills || []).reduce((s, b) => s + (Number(b.amount) || 0), 0);
  const addBillRow = () => setForm((f) => ({ ...f, bills: [...(f.bills || []), blankBill()] }));
  const addBillFromInvoice = (invId) => {
    const inv = invoices.find((i) => i.id === invId);
    if (!inv) return;
    const row = {
      branch: inv.office || "Chennai",
      bill_no: String(inv.invoice_no || "").replace(/^INV[-\s]?/i, ""),
      bill_date: inv.issue_date || "",
      cn_no: "",
      cn_date: "",
      from_place: inv.from_place || inv.origin || "",
      destination: inv.destination || "",
      amount: inv.total || 0,
    };
    setForm((f) => {
      const bills = [...(f.bills || []), row];
      return { ...f, bills, amount: billsTotal(bills), customer_id: f.customer_id || inv.customer_id || "", customer_name: f.customer_name || inv.customer_name || "" };
    });
  };
  const updateBillRow = (idx, key, val) =>
    setForm((f) => {
      const bills = (f.bills || []).map((b, i) => (i === idx ? { ...b, [key]: val } : b));
      return { ...f, bills, amount: billsTotal(bills) };
    });
  const removeBillRow = (idx) =>
    setForm((f) => {
      const bills = (f.bills || []).filter((_, i) => i !== idx);
      return { ...f, bills, amount: billsTotal(bills) };
    });

  const normDate = (s) => {
    s = String(s || "").trim();
    if (!s) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const m = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})$/);
    if (m) {
      let [, d, mo, y] = m;
      if (y.length === 2) y = "20" + y;
      return `${y}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
    }
    return "";
  };
  const pasteRows = async () => {
    let text = "";
    try { text = await navigator.clipboard.readText(); } catch (e) { /* ignore */ }
    if (!text) text = window.prompt("Paste rows from Excel — columns: Branch, LR No., LR Date, From, Destination, Bill No., Bill Date, Bill Amount") || "";
    if (!text.trim()) return;
    const newRows = text.trim().split(/\r?\n/).map((line) => {
      const c = line.split("\t");
      return {
        branch: (c[0] || "Chennai").trim() || "Chennai",
        cn_no: (c[1] || "").trim(),
        cn_date: normDate(c[2]),
        from_place: (c[3] || "").trim(),
        destination: (c[4] || "").trim(),
        bill_no: (c[5] || "").trim(),
        bill_date: normDate(c[6]),
        amount: Number(String(c[7] || "").replace(/[^0-9.-]/g, "")) || 0,
      };
    });
    setForm((f) => {
      const bills = [...(f.bills || []), ...newRows];
      return { ...f, bills, amount: billsTotal(bills) };
    });
    toast.success(`${newRows.length} row(s) pasted`);
  };

  const save = async () => {
    if (mode === "single" && !form.invoice_id && !form.invoice_no) {
      toast.error("Select a bill or enter a Bill No.");
      return;
    }
    if (mode === "multi" && !(form.bills || []).length) {
      toast.error("Add at least one bill row");
      return;
    }
    setSaving(true);
    const payload = mode === "multi" ? { ...form } : { ...form, bills: [] };
    try {
      const res = editing
        ? await api.put(`/bill-submissions/${editing.id}`, payload)
        : await api.post("/bill-submissions", payload);
      const saved = res.data;
      toast.success(editing ? `Submission ${saved.submission_no || ""} updated` : `Submission ${saved.submission_no || ""} recorded`, {
        description: "Do you want to print the slip?",
        duration: 8000,
        action: { label: "Print", onClick: () => printBillSubmission(saved) },
      });
      setOpen(false);
      clearDraft();
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm("Delete this submission record?")) return;
    try {
      await api.delete(`/bill-submissions/${row.id}`);
      toast.success("Submission deleted");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const exportCsv = () => {
    const list = shown;
    const header = "SL No.,Branch,Sub. No.,Sub. Date,Bill No.,Bill Date,Customer Name,Customer Code,Bill Amount,Submitted To,Expected,Status,Slip Uploaded";
    const csv = [
      header,
      ...list.map((s, i) =>
        [
          i + 1,
          s.office || (s.bills && s.bills[0] && s.bills[0].branch) || "",
          String(s.submission_no || "").replace(/^BS[-\s]?/i, ""),
          s.date,
          String(s.invoice_no || "").replace(/^INV[-\s]?/i, ""),
          s.bill_date,
          s.customer_name,
          custCode(s),
          s.amount,
          s.submitted_to,
          s.expected_date,
          s.status,
          s.slip_path ? "Yes" : "No",
        ]
          .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
          .join(",")
      ),
    ].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `bill-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const custPhone = (r) => {
    const c = customers.find((x) => x.id === r.customer_id) || customers.find((x) => (x.name || "").toLowerCase() === (r.customer_name || "").toLowerCase());
    return String(c?.phone || "").replace(/\D/g, "");
  };
  const subBillLines = (r) => {
    if (r.bills && r.bills.length)
      return r.bills.map((b, i) => `${i + 1}. Bill ${b.bill_no || "—"}${b.bill_date ? ` dt ${fmtDate(b.bill_date)}` : ""}${b.destination ? ` (${b.from_place || "—"} → ${b.destination})` : ""} — ₹${(Number(b.amount) || 0).toLocaleString("en-IN")}`);
    return [`1. Bill ${String(r.invoice_no || "").replace(/^INV[-\s]?/i, "") || "—"}${r.bill_date ? ` dt ${fmtDate(r.bill_date)}` : ""}${r.destination ? ` (${r.from_place || "—"} → ${r.destination})` : ""} — ₹${(Number(r.amount) || 0).toLocaleString("en-IN")}`];
  };
  const waLink = (ph, msg) => (ph ? `https://wa.me/91${ph.slice(-10)}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`);
  const waSubmission = (r) => {
    const msg = [
      "*Nisha Transport Corporation — Bill Submission*",
      "",
      `Submission No: ${String(r.submission_no || "").replace(/^BS[-\s]?/i, "") || "—"}`,
      `Date: ${fmtDate(r.date)}`,
      `Branch: ${r.office || (r.bills && r.bills[0] && r.bills[0].branch) || "Chennai"}`,
      `Customer: ${r.customer_name || "—"}${r.customer_code ? ` (${r.customer_code})` : ""}`,
      "",
      ...subBillLines(r),
      "",
      `*Total Amount: ₹${(Number(r.amount) || 0).toLocaleString("en-IN")}*`,
      r.submitted_to ? `Submitted To: ${r.submitted_to}` : "",
      r.expected_date ? `Payment Expected By: ${fmtDate(r.expected_date)}` : "",
      "",
      "Kindly acknowledge receipt of the above bill(s). Thank you!",
    ].filter((l) => l !== "").join("\n");
    window.open(waLink(custPhone(r), msg), "_blank");
  };
  const daysOverdue = (r) => Math.floor((new Date(today()) - new Date(r.expected_date)) / 86400000);
  const overdue = (rows || [])
    .filter((r) => r.expected_date && r.expected_date < today() && !["Cleared", "Returned"].includes(r.status))
    .sort((a, b) => (a.expected_date || "").localeCompare(b.expected_date || ""));
  const overdueTotal = overdue.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const waFollowup = (r) => {
    const d = daysOverdue(r);
    const msg = [
      "*Nisha Transport Corporation — Payment Reminder*",
      "",
      `Dear ${r.customer_name || "Sir/Madam"},`,
      "",
      `Our bill submission ${String(r.submission_no || "").replace(/^BS[-\s]?/i, "")} dated ${fmtDate(r.date)}${r.submitted_to ? ` (handed over to ${r.submitted_to})` : ""} is pending payment:`,
      "",
      ...subBillLines(r),
      "",
      `*Total Amount Due: ₹${(Number(r.amount) || 0).toLocaleString("en-IN")}*`,
      `Payment was expected by ${fmtDate(r.expected_date)} — *${d} day(s) overdue*.`,
      "",
      "Kindly arrange the payment at the earliest. Thank you!",
    ].join("\n");
    window.open(waLink(custPhone(r), msg), "_blank");
  };

  const openCallLog = (r) => {
    setCallRow(r);
    setCallForm({ date: today(), outcome: "", next_call_date: "", notes: "" });
  };
  const saveCallLog = async () => {
    if (!callForm.outcome && !callForm.notes.trim()) {
      toast.error("Pick an outcome or write a note");
      return;
    }
    setCallSaving(true);
    try {
      const res = await api.post(`/bill-submissions/${callRow.id}/call-logs`, callForm);
      const updated = { ...callRow, call_logs: [...(callRow.call_logs || []), res.data] };
      setCallRow(updated);
      setRows((p) => (p || []).map((x) => (x.id === callRow.id ? updated : x)));
      setCallForm({ date: today(), outcome: "", next_call_date: "", notes: "" });
      toast.success("Call note saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCallSaving(false);
    }
  };
  const deleteCallLog = async (logId) => {
    try {
      await api.delete(`/bill-submissions/${callRow.id}/call-logs/${logId}`);
      const updated = { ...callRow, call_logs: (callRow.call_logs || []).filter((l) => l.id !== logId) };
      setCallRow(updated);
      setRows((p) => (p || []).map((x) => (x.id === callRow.id ? updated : x)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const lastCall = (r) => (r.call_logs && r.call_logs.length ? r.call_logs[r.call_logs.length - 1] : null);
  const brokenPromises = (r) =>
    ["Cleared", "Returned"].includes(r.status)
      ? 0
      : (r.call_logs || []).filter(
          (l) => ["Payment Promised", "Cheque Ready"].includes(l.outcome) && (l.next_call_date || l.date) && (l.next_call_date || l.date) < today()
        ).length;

  const monthLabel = (m) => (m ? new Date(`${m}-01`).toLocaleString("en-IN", { month: "long", year: "numeric" }) : "");
  const outcomeReport = (() => {
    const byCust = {};
    (rows || []).forEach((s) => {
      (s.call_logs || []).forEach((l) => {
        if (!String(l.date || "").startsWith(reportMonth)) return;
        const key = s.customer_name || "—";
        const a = (byCust[key] = byCust[key] || { customer: key, calls: 0, outcomes: {}, last_date: "", subs: new Set() });
        a.calls += 1;
        const o = l.outcome || "Other";
        a.outcomes[o] = (a.outcomes[o] || 0) + 1;
        if ((l.date || "") > a.last_date) a.last_date = l.date || "";
        a.subs.add(s.id);
      });
    });
    const pendingByCust = {};
    (rows || []).forEach((s) => {
      if (["Cleared", "Returned"].includes(s.status)) return;
      const key = s.customer_name || "—";
      pendingByCust[key] = (pendingByCust[key] || 0) + (Number(s.amount) || 0);
    });
    return Object.values(byCust)
      .map((a) => ({ ...a, pending: pendingByCust[a.customer] || 0 }))
      .sort((a, b) => b.calls - a.calls);
  })();
  const reportTotalCalls = outcomeReport.reduce((s, r) => s + r.calls, 0);
  const brokenReport = (() => {
    const out = [];
    (rows || []).forEach((s) => {
      if (["Cleared", "Returned"].includes(s.status)) return;
      (s.call_logs || []).forEach((l) => {
        if (!["Payment Promised", "Cheque Ready"].includes(l.outcome)) return;
        const due = l.next_call_date || l.date;
        if (!due || due >= today() || !String(l.date || "").startsWith(reportMonth)) return;
        out.push({
          id: `${s.id}-${l.id}`,
          customer: s.customer_name || "—",
          sub_no: String(s.submission_no || "—").replace(/^BS[-\s]?/i, ""),
          promised_on: l.date,
          expected_by: l.next_call_date || "",
          amount: Number(s.amount) || 0,
          days: Math.floor((new Date(today()) - new Date(due)) / 86400000),
        });
      });
    });
    return out.sort((a, b) => b.days - a.days);
  })();

  const summarySubs = (rows || []).filter((r) => String(r.date || "").startsWith(summaryMonth));
  const summaryStats = (() => {
    const amt = (r) => Number(r.amount) || 0;
    const cleared = summarySubs.filter((r) => r.status === "Cleared");
    const returned = summarySubs.filter((r) => r.status === "Returned");
    const pending = summarySubs.filter((r) => !["Cleared", "Returned"].includes(r.status));
    return {
      count: summarySubs.length,
      total: summarySubs.reduce((s, r) => s + amt(r), 0),
      clearedCount: cleared.length,
      cleared: cleared.reduce((s, r) => s + amt(r), 0),
      pendingCount: pending.length,
      pending: pending.reduce((s, r) => s + amt(r), 0),
      returnedCount: returned.length,
      returned: returned.reduce((s, r) => s + amt(r), 0),
    };
  })();
  const summaryCustRows = (() => {
    const by = {};
    summarySubs.forEach((r) => {
      const a = (by[r.customer_name || "—"] = by[r.customer_name || "—"] || { customer: r.customer_name || "—", subs: 0, total: 0, cleared: 0, pending: 0 });
      a.subs += 1;
      a.total += Number(r.amount) || 0;
      if (r.status === "Cleared") a.cleared += Number(r.amount) || 0;
      else if (r.status !== "Returned") a.pending += Number(r.amount) || 0;
    });
    return Object.values(by).sort((a, b) => b.pending - a.pending);
  })();

  return (
    <div>
      <PageHeader
        title="Bill Submission"
        subtitle="Register of bills handed over to customers — track who received them and when payment is expected."
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setSummaryOpen(true)} data-testid="collection-summary-btn">
              <BarChart3 className="mr-2 h-4 w-4" /> Summary
            </Button>
            <Button variant="outline" onClick={() => setReportOpen(true)} data-testid="call-report-btn">
              <PhoneCall className="mr-2 h-4 w-4" /> Call Report
            </Button>
            <Button
              variant="outline"
              className={overdue.length ? "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 hover:text-amber-900" : ""}
              onClick={() => setFollowupOpen(true)}
              data-testid="submission-followup-btn"
            >
              <BellRing className="mr-2 h-4 w-4" /> Follow-ups{overdue.length ? ` (${overdue.length})` : ""}
            </Button>
            <Button variant="outline" onClick={() => printBillSubmissionRegister(shown.filter((r) => selected.has(r.id)).length ? shown.filter((r) => selected.has(r.id)) : shown)} disabled={!shown.length} data-testid="submission-print-list-btn">
              <Printer className="mr-2 h-4 w-4" /> Print{shown.filter((r) => selected.has(r.id)).length ? ` (${shown.filter((r) => selected.has(r.id)).length})` : " List"}
            </Button>
            <Button variant="outline" onClick={exportCsv} disabled={!shown.length} data-testid="submission-excel-btn">
              <FileSpreadsheet className="mr-2 h-4 w-4" /> Excel
            </Button>
            <Button data-testid="add-submission-btn" onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" /> Record Submission
            </Button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-border bg-slate-50 px-4 py-3" data-testid="submission-filter-bar">
        <div className="grid gap-1">
          <Label className="text-xs">Search</Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input
              className="h-9 w-[210px] bg-white pl-8 pr-7"
              placeholder="Customer / code / bill no."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="submission-search"
            />
            {q && (
              <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => setQ("")} data-testid="submission-search-clear">
                ✕
              </button>
            )}
          </div>
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">Date From</Label>
          <Input type="date" className="h-9 w-[150px] bg-white" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="submission-filter-from" />
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">To</Label>
          <Input type="date" className="h-9 w-[150px] bg-white" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="submission-filter-to" />
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className={`h-9 text-xs font-bold ${noBranchOnly ? "bg-amber-500 text-white hover:bg-amber-600" : "border border-amber-300 bg-white text-amber-700 hover:bg-amber-50"}`}
          onClick={() => setNoBranchOnly((v) => !v)}
          data-testid="submission-no-branch-toggle"
        >
          {noBranchOnly ? "✓ " : ""}No Branch only ({(rows || []).filter((r) => branchOf(r) === "—").length})
        </Button>
        {(fromDate || toDate || q) && (
          <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => { setFromDate(""); setToDate(""); setQ(""); }} data-testid="submission-filter-clear">
            Clear
          </Button>
        )}
        {(() => {
          const overdue = (rows || []).filter(
            (r) => r.expected_date && String(r.expected_date).slice(0, 10) < today() && !/paid|cleared|settled|received/i.test(r.status || "")
          );
          const chase = () => {
            const msg = [
              "*NTC — Overdue Bill Submissions*",
              "Payment expected date crossed:",
              ...overdue.slice(0, 15).map((r, i) => `${i + 1}. Sub ${String(r.submission_no || "—").replace(/^BS[-\s]?/i, "")} — ${r.customer_name || "—"}${custCode(r) ? ` (${custCode(r)})` : ""} — Bill ${String(r.invoice_no || "—").replace(/^INV[-\s]?/i, "")} — ₹${(Number(r.amount) || 0).toLocaleString("en-IN")} — expected ${fmtDate(r.expected_date)} (*${Math.floor((new Date(today()) - new Date(String(r.expected_date).slice(0, 10))) / 86400000)}d overdue*)`),
              ...(overdue.length > 15 ? [`…and ${overdue.length - 15} more`] : []),
              "",
              `Total overdue: ₹${overdue.reduce((s, r) => s + (Number(r.amount) || 0), 0).toLocaleString("en-IN")} — ${overdue.length} submission(s). Please follow up for payment.`,
              "",
              "— Nisha Transport Corporation",
            ].join("\n");
            window.open(`https://wa.me/${HEAD_OFFICE.whatsapp}?text=${encodeURIComponent(msg)}`, "_blank");
          };
          return overdue.length ? (
            <Button variant="outline" size="sm" className="h-9 border-red-300 bg-red-50 text-xs font-bold text-red-700 hover:bg-red-100" onClick={chase} data-testid="overdue-chase-btn">
              <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> Chase Overdue ({overdue.length})
            </Button>
          ) : null;
        })()}
        <span className="ml-auto text-xs font-semibold text-slate-500" data-testid="submission-count">{shown.length} submission(s)</span>
      </div>

      {rows !== null && (() => {
        const needle = q.trim().toLowerCase();
        const base = (rows || []).filter((r) => {
          const d = String(r.date || "").slice(0, 10);
          if (fromDate && (!d || d < fromDate)) return false;
          if (toDate && (!d || d > toDate)) return false;
          if (needle) {
            const hay = [r.customer_name, custCode(r), String(r.submission_no || "").replace(/^BS[-\s]?/i, ""), String(r.invoice_no || "").replace(/^INV[-\s]?/i, ""), r.submitted_to, r.office];
            if (!hay.some((x) => String(x || "").toLowerCase().includes(needle))) return false;
          }
          return true;
        });
        const byBranch = {};
        base.forEach((r) => {
          const b = branchOf(r);
          if (!byBranch[b]) byBranch[b] = { total: 0, overdue: 0, items: [], noslip: [] };
          byBranch[b].total++;
          if (isOverdueSub(r)) { byBranch[b].overdue++; byBranch[b].items.push(r); }
          if (!r.slip_path) byBranch[b].noslip.push(r);
        });
        const names = Object.keys(byBranch).sort();
        const showBranchPills = names.length >= 2;
        const slipBranches = names.filter((b) => byBranch[b].noslip.length > 0);
        if (!showBranchPills && !slipBranches.length) return null;
        const chaseBranchOverdue = (b) => {
          const list = byBranch[b].items;
          const digits = String(branchContacts[b] || "").replace(/\D/g, "");
          const days = (r) => Math.floor((new Date(today()) - new Date(String(r.expected_date).slice(0, 10))) / 86400000);
          const lines = list
            .slice(0, 30)
            .map((r, i) => `${i + 1}. ${r.customer_name || "—"} — Bill ${String(r.invoice_no || "").replace(/^INV[-\s]?/i, "") || "—"} ₹${(Number(r.amount) || 0).toLocaleString("en-IN")}, expected ${fmtDate(r.expected_date)} — *overdue ${days(r)}d*`);
          const total = list.reduce((s, r) => s + (Number(r.amount) || 0), 0);
          const msg = `*NTC — OVERDUE SUBMISSIONS (${b.toUpperCase()} BRANCH)*\n\n${lines.join("\n")}${list.length > 30 ? `\n…and ${list.length - 30} more` : ""}\n\n*Total overdue: ₹${total.toLocaleString("en-IN")}* (${list.length} submission${list.length === 1 ? "" : "s"}). Please follow up for payment today.`;
          if (!digits) toast.info(`No WhatsApp number for ${b} — set it in Settings → Branch WhatsApp Contacts. Opening WhatsApp without a number.`);
          window.open(`https://wa.me/${digits ? (digits.length === 10 ? "91" + digits : digits) : ""}?text=${encodeURIComponent(msg)}`, "_blank");
        };
        const chaseBranchNoSlip = (b) => {
          const list = byBranch[b].noslip;
          const digits = String(branchContacts[b] || "").replace(/\D/g, "");
          const lines = list
            .slice(0, 30)
            .map((r, i) => `${i + 1}. Sub ${String(r.submission_no || "").replace(/^BS[-\s]?/i, "") || "—"} — ${r.customer_name || "—"}, Bill ${String(r.invoice_no || "").replace(/^INV[-\s]?/i, "") || "—"} ₹${(Number(r.amount) || 0).toLocaleString("en-IN")}${r.date ? `, submitted ${fmtDate(r.date)}` : ""}`);
          const msg = `*NTC — SUBMISSION SLIP PENDING (${b.toUpperCase()} BRANCH)*\n\n${lines.join("\n")}${list.length > 30 ? `\n…and ${list.length - 30} more` : ""}\n\n*Total: ${list.length} submission(s) without an uploaded slip.* Please upload the acknowledged submission slips today.`;
          if (!digits) toast.info(`No WhatsApp number for ${b} — set it in Settings → Branch WhatsApp Contacts. Opening WhatsApp without a number.`);
          window.open(`https://wa.me/${digits ? (digits.length === 10 ? "91" + digits : digits) : ""}?text=${encodeURIComponent(msg)}`, "_blank");
        };
        return (
          <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="submission-branch-pills">
            {showBranchPills && (
              <>
            <span className="rounded-full border border-slate-300 bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600">Submissions by branch:</span>
            {names.map((b) => {
              const s = byBranch[b];
              const cls = s.overdue > 0 ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-800";
              const active = branchPick === b;
              return (
                <span key={b} className={`inline-flex items-center overflow-hidden rounded-full ${s.overdue > 0 ? "bg-red-100" : "bg-emerald-100"}`}>
                  <button
                    type="button"
                    className={`px-2.5 py-1 text-[11px] font-bold transition-transform hover:scale-105 ${cls} ${active ? "ring-2 ring-inset ring-slate-700" : ""}`}
                    title={active ? "Tap again to show all branches" : `Show only ${b} submissions`}
                    onClick={() => setBranchPick(active ? "All" : b)}
                    data-testid={`submission-pill-${b.toLowerCase()}`}
                  >
                    {b}: {s.total}{s.overdue > 0 ? ` (${s.overdue} overdue)` : ""}
                  </button>
                  {s.overdue > 0 && (
                    <button
                      type="button"
                      className="border-l border-red-200 px-2 py-1 text-emerald-700 hover:bg-emerald-50"
                      title={`WhatsApp ${b}'s overdue submissions to the branch`}
                      onClick={() => chaseBranchOverdue(b)}
                      data-testid={`submission-pill-wa-${b.toLowerCase()}`}
                    >
                      <MessageCircle className="h-3.5 w-3.5" />
                    </button>
                  )}
                </span>
              );
            })}
            <span className="mx-1 h-4 w-px bg-slate-300" />
              </>
            )}
            <span className="rounded-full border border-slate-300 bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600">Slip pending:</span>
            {slipBranches.length === 0 && (
              <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-bold text-emerald-800" data-testid="slip-all-clear">All slips uploaded ✓</span>
            )}
            {slipBranches
              .map((b) => (
                <span key={`slip-${b}`} className="inline-flex items-center overflow-hidden rounded-full bg-amber-100">
                  <span className="px-2.5 py-1 text-[11px] font-bold text-amber-800" data-testid={`slip-pill-${b.toLowerCase()}`}>
                    {b}: {byBranch[b].noslip.length} no slip
                  </span>
                  <button
                    type="button"
                    className="border-l border-amber-200 px-2 py-1 text-emerald-700 hover:bg-emerald-50"
                    title={`WhatsApp ${b}'s missing-slip list to the branch`}
                    onClick={() => chaseBranchNoSlip(b)}
                    data-testid={`slip-pill-wa-${b.toLowerCase()}`}
                  >
                    <MessageCircle className="h-3.5 w-3.5" />
                  </button>
                </span>
              ))}
          </div>
        );
      })()}

      <TableShell>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="w-10">
                <Checkbox
                  checked={shown.length > 0 && shown.every((r) => selected.has(r.id))}
                  onCheckedChange={() => setSelected((p) => { const n = new Set(p); const all = shown.every((r) => n.has(r.id)); shown.forEach((r) => (all ? n.delete(r.id) : n.add(r.id))); return n; })}
                  data-testid="submission-select-all"
                  aria-label="Select all"
                />
              </TableHead>
              {["SL No.", "Branch", "Sub. No.", "Sub. Date", "Bill No.", "Bill Date", "Customer Name", "Customer Code", "Bill Amount", "Submitted To", "Payment Expected", "Bill Status", "Submission Attachment"].map((h) => (
                <TableHead key={h} className={`text-[11px] uppercase tracking-[0.15em] text-slate-500 ${h === "Bill Amount" ? "text-right" : ""}`}>
                  {h}
                </TableHead>
              ))}
              <TableHead className="w-[90px] text-right text-[11px] uppercase tracking-[0.15em] text-slate-500">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null &&
              [1, 2, 3].map((i) => (
                <TableRow key={i}>
                  {Array.from({ length: 14 }).map((_, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-16" /></TableCell>
                  ))}
                </TableRow>
              ))}
            {rows && shown.length === 0 && (
              <TableRow>
                <TableCell colSpan={15} className="py-12 text-center text-muted-foreground">
                  {rows.length === 0 ? "No submissions recorded yet. Click “Record Submission” to add one." : "No submissions match your search / date filter."}
                </TableCell>
              </TableRow>
            )}
            {rows &&
              shown.map((r, i) => (
                <TableRow key={r.id} data-testid="bill-submissions-row" data-state={selected.has(r.id) ? "selected" : undefined}>
                  <TableCell className="w-10">
                    <Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggleRow(r.id)} data-testid="submission-select-row" aria-label="Select row" />
                  </TableCell>
                  <TableCell className="text-sm">{i + 1}</TableCell>
                  <TableCell className="text-sm" data-testid="submission-branch-cell"><BranchCell value={r.office || (r.bills && r.bills[0] && r.bills[0].branch)} /></TableCell>
                  <TableCell className="font-medium">{String(r.submission_no || "").replace(/^BS[-\s]?/i, "") || "—"}</TableCell>
                  <TableCell className="text-sm">{fmtDate(r.date)}</TableCell>
                  <TableCell className="text-sm">{String(r.invoice_no || "").replace(/^INV[-\s]?/i, "") || "—"}</TableCell>
                  <TableCell className="text-sm">{r.bill_date ? fmtDate(r.bill_date) : "—"}</TableCell>
                  <TableCell className="text-sm">{r.customer_name || "—"}</TableCell>
                  <TableCell className="text-sm font-medium" data-testid="submission-cust-code">{custCode(r) || "—"}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{inr(r.amount)}</TableCell>
                  <TableCell className="text-sm">{r.submitted_to || "—"}</TableCell>
                  <TableCell className="text-sm">
                    <span className="inline-flex items-center gap-1.5">
                      {fmtDate(r.expected_date)}
                      {r.expected_date && String(r.expected_date).slice(0, 10) < today() && !/paid|cleared|settled|received/i.test(r.status || "") && (
                        <span className="rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold text-white" title={`Payment expected on ${fmtDate(r.expected_date)} — overdue`} data-testid="submission-overdue-badge">
                          Overdue {Math.floor((new Date(today()) - new Date(String(r.expected_date).slice(0, 10))) / 86400000)}d
                        </span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_CLR[r.status] || "border-slate-200 text-slate-600"}`}>
                      {r.status}
                    </span>
                  </TableCell>
                  <TableCell data-testid="submission-slip-cell">
                    <input
                      ref={(el) => { slipInputs.current[r.id] = el; }}
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp,.pdf"
                      className="hidden"
                      onChange={(e) => onSlipFile(r, e)}
                      data-testid="submission-slip-file-input"
                    />
                    {r.slip_path ? (
                      <span className="inline-flex items-center gap-1">
                        <a href={slipUrl(r)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 hover:underline" title={r.slip_filename || "View submission slip"} data-testid="submission-slip-view">
                          <FileCheck2 className="h-3.5 w-3.5" /> Slip <ExternalLink className="h-3 w-3" />
                        </a>
                        <button type="button" className="ml-1 text-slate-400 hover:text-red-600" title="Remove slip" onClick={() => deleteSlip(r)} data-testid="submission-slip-delete">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-[11px]"
                        disabled={slipUploadingId === r.id}
                        onClick={() => slipInputs.current[r.id]?.click()}
                        data-testid="submission-slip-upload-btn"
                      >
                        <Upload className="mr-1 h-3 w-3" /> {slipUploadingId === r.id ? "Uploading…" : "Upload Slip"}
                      </Button>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="relative h-8 w-8 text-sky-600 hover:text-sky-700" title="Follow-up call log" onClick={() => openCallLog(r)} data-testid="submission-call-log-btn">
                        <Phone className="h-4 w-4" />
                        {(r.call_logs || []).length > 0 && (
                          <span className="absolute -right-0.5 -top-0.5 rounded-full bg-sky-600 px-1 text-[9px] font-bold leading-3 text-white">{r.call_logs.length}</span>
                        )}
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600 hover:text-emerald-700" title="WhatsApp slip summary" onClick={() => waSubmission(r)} data-testid="submission-wa-btn">
                        <MessageCircle className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-primary" title="Print slip" onClick={() => printBillSubmission(r)} data-testid="print-submission-btn">
                        <Printer className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(r)} data-testid="edit-submission-btn">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => remove(r)} data-testid="delete-submission-btn">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableShell>

      {open && dlgView === "min" && (
        <div className="fixed bottom-4 right-4 z-[60] flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 shadow-xl" data-testid="submission-dialog-minbar">
          <FileSpreadsheet className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">{editing ? "Edit Submission" : "Record Bill Submission"}</span>
          <Button variant="ghost" size="icon" className="h-7 w-7" title="Restore" onClick={() => setDlgView("normal")} data-testid="submission-dialog-restore">
            <Maximize2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" title="Close" onClick={() => { clearDraft(); setOpen(false); setDlgView("normal"); }} data-testid="submission-dialog-discard">
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      <Dialog open={open && dlgView !== "min"} onOpenChange={(v) => { setOpen(v); if (!v) { setDlgView("normal"); clearDraft(); } }}>
        <DialogContent className={dlgView === "max" ? "h-[95vh] max-h-[95vh] w-[97vw] max-w-[97vw] overflow-y-auto" : `max-h-[92vh] overflow-y-auto ${mode === "multi" ? "sm:max-w-[960px]" : "sm:max-w-[560px]"}`}>
          <DialogHeader>
            <div className="flex items-center justify-between pr-8">
              <DialogTitle className="font-display">{editing ? "Edit Submission" : "Record Bill Submission"}</DialogTitle>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" title="Minimize" onClick={() => { saveDraft(); setDlgView("min"); }} data-testid="submission-dialog-minimize">
                  <Minus className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" title={dlgView === "max" ? "Restore size" : "Maximize"} onClick={() => setDlgView(dlgView === "max" ? "normal" : "max")} data-testid="submission-dialog-maximize">
                  {dlgView === "max" ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="flex gap-2">
              <Button type="button" size="sm" variant={mode === "single" ? "default" : "outline"} className="h-7 text-xs" onClick={() => setMode("single")} data-testid="submission-single-mode">
                Single Bill
              </Button>
              <Button type="button" size="sm" variant={mode === "multi" ? "default" : "outline"} className="h-7 text-xs" onClick={() => setMode("multi")} data-testid="submission-multi-mode">
                Multiple Bills
              </Button>
            </div>
            {mode === "single" && (
            <>
            <div className="grid gap-1.5">
              <Label>Bill / Invoice</Label>
              <div className="mb-1 flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={entryMode === "select" ? "default" : "outline"}
                  className="h-7 text-xs"
                  onClick={() => setEntryMode("select")}
                  data-testid="submission-mode-select"
                >
                  Select a Bill
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={entryMode === "manual" ? "default" : "outline"}
                  className="h-7 text-xs"
                  onClick={() => setEntryMode("manual")}
                  data-testid="submission-mode-manual"
                >
                  Enter Manually
                </Button>
              </div>
              {entryMode === "select" ? (
                <Select value={form.invoice_id} onValueChange={pickInvoice}>
                  <SelectTrigger data-testid="submission-invoice"><SelectValue placeholder="Select a bill" /></SelectTrigger>
                  <SelectContent>
                    {invoices.map((i) => (
                      <SelectItem key={i.id} value={i.id}>
                        {String(i.invoice_no || "").replace(/^INV[-\s]?/i, "")} — {i.customer_name || "—"} ({inr(i.total)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="grid gap-1.5">
                    <Label className="text-xs text-muted-foreground">Bill No.</Label>
                    <Input data-testid="submission-manual-billno" placeholder="e.g. INV-1001" value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_id: "", invoice_no: e.target.value })} />
                  </div>
                  <div className="grid gap-1.5">
                    <Label className="text-xs text-muted-foreground">Customer name</Label>
                    <Input data-testid="submission-manual-customer" placeholder="Customer" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
                  </div>
                </div>
              )}
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="grid gap-1.5">
                <Label>LR No.</Label>
                <Input data-testid="submission-cn-no" placeholder="e.g. 1001" value={form.cn_no} onChange={(e) => setForm({ ...form, cn_no: e.target.value })} />
              </div>
              <div className="grid gap-1.5">
                <Label>LR Date</Label>
                <Input type="date" data-testid="submission-cn-date" value={form.cn_date} onChange={(e) => setForm({ ...form, cn_date: e.target.value })} />
              </div>
              <div className="grid gap-1.5">
                <Label>From</Label>
                <Input data-testid="submission-from" placeholder="Chennai" value={form.from_place} onChange={(e) => setForm({ ...form, from_place: e.target.value })} />
              </div>
              <div className="grid gap-1.5">
                <Label>Destination</Label>
                <Input data-testid="submission-destination" placeholder="Bangalore" value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} />
              </div>
              <div className="grid gap-1.5">
                <Label>Bill Date</Label>
                <Input type="date" data-testid="submission-bill-date" value={form.bill_date} onChange={(e) => setForm({ ...form, bill_date: e.target.value })} />
              </div>
              <div className="grid gap-1.5">
                <Label>Customer Code</Label>
                <Input
                  data-testid="submission-customer-code"
                  placeholder="e.g. CUST-1001"
                  value={form.customer_code || ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    const key = v.trim().toLowerCase();
                    const c = key ? customers.find((x) => (x.customer_code || "").toLowerCase() === key) : null;
                    setForm((f) => ({ ...f, customer_code: v, ...(c ? { customer_name: c.name, customer_id: c.id, customer_address: c.address || f.customer_address } : {}) }));
                  }}
                />
              </div>
              <div className="grid gap-1.5 sm:col-span-3">
                <Label>Customer Address</Label>
                <Input data-testid="submission-customer-address" placeholder="Customer address" value={form.customer_address || ""} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} />
              </div>
            </div>
            </>
            )}

            {mode === "multi" && (
            <div className="grid gap-2">
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="grid gap-1.5">
                  <Label>Customer Code</Label>
                  <Input
                    placeholder="e.g. CUST-1001"
                    value={form.customer_code || ""}
                    data-testid="submission-multi-customer-code"
                    onChange={(e) => {
                      const v = e.target.value;
                      const key = v.trim().toLowerCase();
                      const c = key ? customers.find((x) => (x.customer_code || "").toLowerCase() === key) : null;
                      setForm((f) => ({ ...f, customer_code: v, ...(c ? { customer_name: c.name, customer_id: c.id, customer_address: c.address || f.customer_address } : {}) }));
                    }}
                  />
                </div>
                <div className="grid gap-1.5 sm:col-span-2">
                  <Label>Customer</Label>
                  <Input placeholder="Customer name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="submission-multi-customer" />
                </div>
              </div>
              <div className="grid gap-1.5">
                <Label>Customer Address</Label>
                <Input placeholder="Customer address" value={form.customer_address || ""} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} data-testid="submission-multi-customer-address" />
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label className="text-sm font-semibold">Bills in this submission</Label>
                <div className="flex items-center gap-2">
                  <Select value="" onValueChange={addBillFromInvoice}>
                    <SelectTrigger className="h-8 w-[220px] text-xs" data-testid="submission-add-bill-select"><SelectValue placeholder="+ Add existing bill" /></SelectTrigger>
                    <SelectContent>
                      {invoices.length === 0 && <SelectItem value="none" disabled>No bills</SelectItem>}
                      {invoices.map((i) => (
                        <SelectItem key={i.id} value={i.id}>
                          {String(i.invoice_no || "").replace(/^INV[-\s]?/i, "")} — {inr(i.total)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={addBillRow} data-testid="submission-add-row">
                    <Plus className="mr-1 h-3.5 w-3.5" /> Add row
                  </Button>
                  <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={pasteRows} data-testid="submission-paste-rows">
                    Paste from Excel
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-xs" data-testid="submission-bills-table">
                  <thead className="bg-slate-50">
                    <tr className="[&>th]:whitespace-nowrap [&>th]:px-2 [&>th]:py-2 [&>th]:text-left [&>th]:font-semibold [&>th]:text-slate-500">
                      <th>Sl</th><th>Branch</th><th>LR No.</th><th>LR Date</th><th>From</th><th>Destination</th><th>Bill No.</th><th>Bill Date</th><th className="text-right">Bill Amount</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(form.bills || []).length === 0 && (
                      <tr><td colSpan={10} className="px-2 py-6 text-center text-muted-foreground">No bills yet. Add existing bills or a blank row.</td></tr>
                    )}
                    {(form.bills || []).map((b, idx) => (
                      <tr key={idx} className="border-t [&>td]:px-1.5 [&>td]:py-1" data-testid="submission-bill-row">
                        <td className="text-center">{idx + 1}</td>
                        <td>
                          <Select value={b.branch || "Chennai"} onValueChange={(v) => updateBillRow(idx, "branch", v)}>
                            <SelectTrigger className="h-8 w-[110px] text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>{BSBRANCHES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td><Input className="h-8 w-[100px] text-xs" value={b.cn_no} onChange={(e) => updateBillRow(idx, "cn_no", e.target.value)} /></td>
                        <td><Input type="date" className="h-8 w-[140px] text-xs" value={b.cn_date} onChange={(e) => updateBillRow(idx, "cn_date", e.target.value)} /></td>
                        <td><Input className="h-8 w-[110px] text-xs" value={b.from_place} onChange={(e) => updateBillRow(idx, "from_place", e.target.value)} /></td>
                        <td><Input className="h-8 w-[110px] text-xs" value={b.destination} onChange={(e) => updateBillRow(idx, "destination", e.target.value)} /></td>
                        <td><Input className="h-8 w-[100px] text-xs" value={b.bill_no} onChange={(e) => updateBillRow(idx, "bill_no", e.target.value)} /></td>
                        <td><Input type="date" className="h-8 w-[140px] text-xs" value={b.bill_date} onChange={(e) => updateBillRow(idx, "bill_date", e.target.value)} /></td>
                        <td><Input type="number" className="h-8 w-[100px] text-xs text-right" value={b.amount} onChange={(e) => updateBillRow(idx, "amount", Number(e.target.value))} /></td>
                        <td>
                          <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeBillRow(idx)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-right text-xs text-muted-foreground">{(form.bills || []).length} bill(s) · Total auto-calculated below</div>
            </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label>Submission date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="submission-date" />
              </div>
              <div className="grid gap-1.5">
                <Label>Expected payment date</Label>
                <Input type="date" value={form.expected_date} onChange={(e) => setForm({ ...form, expected_date: e.target.value })} />
              </div>
              <div className="grid gap-1.5">
                <Label>Submitted to (person)</Label>
                <Input placeholder="e.g. Accounts dept, Mr. Sharma" value={form.submitted_to} onChange={(e) => setForm({ ...form, submitted_to: e.target.value })} data-testid="submission-submitted-to" />
              </div>
              <div className="grid gap-1.5">
                <Label>Received by (their staff)</Label>
                <Input placeholder="Receiver name" value={form.received_by} onChange={(e) => setForm({ ...form, received_by: e.target.value })} />
              </div>
              <div className="grid gap-1.5">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="submission-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label>Bill Amount (₹)</Label>
                <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} />
              </div>
            </div>
            <Textarea placeholder="Remarks (optional)" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-submission-btn">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={followupOpen} onOpenChange={setFollowupOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[900px]" data-testid="submission-followup-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <BellRing className="h-5 w-5 text-amber-600" /> Payment Follow-up List
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Submissions past their expected payment date (not yet Cleared/Returned) — for weekly collection calls.
          </p>
          {overdue.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="followup-empty">
              Nothing overdue — all expected payments are on track. 🎉
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-xs" data-testid="followup-table">
                <thead className="bg-amber-50">
                  <tr className="[&>th]:whitespace-nowrap [&>th]:px-2.5 [&>th]:py-2 [&>th]:text-left [&>th]:font-semibold [&>th]:text-amber-900">
                    <th>Sub. No.</th><th>Customer</th><th>Bill No.</th><th className="text-right">Amount</th><th>Submitted To</th><th>Expected</th><th>Overdue</th><th>Last Call</th><th>Status</th><th className="text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {overdue.map((r) => (
                    <tr key={r.id} className="border-t [&>td]:px-2.5 [&>td]:py-2" data-testid="followup-row">
                      <td className="font-medium">{String(r.submission_no || "").replace(/^BS[-\s]?/i, "") || "—"}</td>
                      <td>
                        {r.customer_name || "—"}
                        {brokenPromises(r) > 0 && (
                          <span className="ml-1.5 inline-block rounded-full border border-rose-300 bg-rose-50 px-1.5 py-0.5 text-[10px] font-bold text-rose-700" title="Customer promised payment but it never arrived" data-testid="followup-broken-promise">
                            ⚠ Promise broken{brokenPromises(r) > 1 ? ` ×${brokenPromises(r)}` : ""}
                          </span>
                        )}
                      </td>
                      <td>{r.bills && r.bills.length ? `${r.bills.length} bills` : String(r.invoice_no || "").replace(/^INV[-\s]?/i, "") || "—"}</td>
                      <td className="text-right tabular-nums">{inr(r.amount)}</td>
                      <td>{r.submitted_to || "—"}</td>
                      <td>{fmtDate(r.expected_date)}</td>
                      <td>
                        <span className="inline-block rounded-full bg-rose-50 px-2 py-0.5 font-semibold text-rose-700" data-testid="followup-overdue-days">
                          {daysOverdue(r)}d
                        </span>
                      </td>
                      <td data-testid="followup-last-call">
                        {lastCall(r) ? (
                          <div>
                            <div className="whitespace-nowrap">{fmtDate(lastCall(r).date)} · {lastCall(r).outcome || "—"}</div>
                            {lastCall(r).next_call_date && <div className="text-[10px] text-muted-foreground">Next: {fmtDate(lastCall(r).next_call_date)}</div>}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td>{r.status}</td>
                      <td className="text-right">
                        <div className="flex justify-end gap-0.5">
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-sky-600 hover:text-sky-700" title="Log this call" onClick={() => openCallLog(r)} data-testid="followup-call-log-btn">
                            <Phone className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-600 hover:text-emerald-700" title="WhatsApp payment reminder" onClick={() => waFollowup(r)} data-testid="followup-wa-btn">
                            <MessageCircle className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t bg-slate-50 font-semibold [&>td]:px-2.5 [&>td]:py-2">
                    <td colSpan={3} className="text-right">TOTAL PENDING</td>
                    <td className="text-right tabular-nums" data-testid="followup-total">{inr(overdueTotal)}</td>
                    <td colSpan={6}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => printBillSubmissionRegister(overdue, { title: "PAYMENT FOLLOW-UP LIST" })} disabled={!overdue.length} data-testid="followup-print-btn">
              <Printer className="mr-2 h-4 w-4" /> Print List
            </Button>
            <Button onClick={() => setFollowupOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!callRow} onOpenChange={(v) => !v && setCallRow(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]" data-testid="call-log-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Phone className="h-5 w-5 text-sky-600" /> Call Log — {String(callRow?.submission_no || "").replace(/^BS[-\s]?/i, "")} · {callRow?.customer_name || "—"}
            </DialogTitle>
          </DialogHeader>
          {callRow && brokenPromises(callRow) > 0 && (
            <div className="flex items-center gap-2 rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700" data-testid="call-log-promise-flag">
              <BellRing className="h-4 w-4 shrink-0" />
              <span>
                Promise broken ×{brokenPromises(callRow)} — this customer promised payment
                {(() => {
                  const l = (callRow.call_logs || []).filter((x) => ["Payment Promised", "Cheque Ready"].includes(x.outcome)).pop();
                  return l ? ` on ${fmtDate(l.date)}${l.next_call_date ? ` (expected by ${fmtDate(l.next_call_date)})` : ""}` : "";
                })()}{" "}
                but the bills are still pending. Mention this on the call.
              </span>
            </div>
          )}
          {(callRow?.call_logs || []).length > 0 ? (
            <div className="max-h-48 space-y-1.5 overflow-y-auto rounded-md border border-border bg-slate-50 p-2">
              {[...(callRow.call_logs || [])].reverse().map((l) => (
                <div key={l.id} className="rounded-md bg-white p-2 text-xs shadow-sm" data-testid="call-log-item">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{fmtDate(l.date)}</span>
                    {l.outcome && (
                      <span className={`rounded-full border px-2 py-0.5 font-medium ${OUTCOME_CLR[l.outcome] || "border-slate-200 text-slate-600"}`}>{l.outcome}</span>
                    )}
                    {l.next_call_date && <span className="text-muted-foreground">Next call: {fmtDate(l.next_call_date)}</span>}
                    <span className="ml-auto text-muted-foreground">by {l.by || "—"}</span>
                    <button type="button" className="text-destructive" title="Delete note" onClick={() => deleteCallLog(l.id)} data-testid="delete-call-log-btn">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {l.notes && <p className="mt-1 text-slate-600">{l.notes}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="call-log-empty">No calls logged yet for this submission.</p>
          )}
          <div className="grid gap-3 rounded-md border border-border p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Log a new call</p>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="grid gap-1.5">
                <Label className="text-xs">Call Date</Label>
                <Input type="date" value={callForm.date} onChange={(e) => setCallForm((f) => ({ ...f, date: e.target.value }))} data-testid="call-log-date" />
              </div>
              <div className="grid gap-1.5">
                <Label className="text-xs">Outcome</Label>
                <Select value={callForm.outcome} onValueChange={(v) => setCallForm((f) => ({ ...f, outcome: v }))}>
                  <SelectTrigger data-testid="call-log-outcome"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {OUTCOMES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label className="text-xs">Next Call Date</Label>
                <Input type="date" value={callForm.next_call_date} onChange={(e) => setCallForm((f) => ({ ...f, next_call_date: e.target.value }))} data-testid="call-log-next-date" />
              </div>
            </div>
            <Textarea rows={2} placeholder="Notes — who you spoke to, what they said…" value={callForm.notes} onChange={(e) => setCallForm((f) => ({ ...f, notes: e.target.value }))} data-testid="call-log-notes" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCallRow(null)}>Close</Button>
            <Button onClick={saveCallLog} disabled={callSaving} data-testid="save-call-log-btn">
              {callSaving ? "Saving…" : "Save Call Note"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[760px]" data-testid="call-report-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <PhoneCall className="h-5 w-5 text-sky-600" /> Call Outcome Report
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap items-end gap-3">
            <div className="grid gap-1.5">
              <Label className="text-xs">Month</Label>
              <Input type="month" className="h-9 w-[170px]" value={reportMonth} onChange={(e) => setReportMonth(e.target.value)} data-testid="call-report-month" />
            </div>
            <p className="pb-2 text-xs text-muted-foreground">
              {reportTotalCalls} call(s) logged across {outcomeReport.length} customer(s) in {monthLabel(reportMonth)}
            </p>
          </div>
          {outcomeReport.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="call-report-empty">
              No calls logged in {monthLabel(reportMonth)}.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-xs" data-testid="call-report-table">
                <thead className="bg-sky-50">
                  <tr className="[&>th]:whitespace-nowrap [&>th]:px-2.5 [&>th]:py-2 [&>th]:text-left [&>th]:font-semibold [&>th]:text-sky-900">
                    <th>Customer</th><th className="text-center">Calls</th><th>Outcomes</th><th>Last Call</th><th className="text-right">Pending Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {outcomeReport.map((r) => (
                    <tr key={r.customer} className="border-t [&>td]:px-2.5 [&>td]:py-2" data-testid="call-report-row">
                      <td className="font-medium">{r.customer}</td>
                      <td className="text-center font-semibold">{r.calls}</td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(r.outcomes).map(([o, n]) => (
                            <span key={o} className={`rounded-full border px-2 py-0.5 font-medium ${OUTCOME_CLR[o] || "border-slate-200 text-slate-600"}`}>
                              {o}{n > 1 ? ` ×${n}` : ""}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="whitespace-nowrap">{r.last_date ? fmtDate(r.last_date) : "—"}</td>
                      <td className="text-right tabular-nums">{inr(r.pending)}</td>
                    </tr>
                  ))}
                  <tr className="border-t bg-slate-50 font-semibold [&>td]:px-2.5 [&>td]:py-2">
                    <td className="text-right">TOTAL</td>
                    <td className="text-center" data-testid="call-report-total">{reportTotalCalls}</td>
                    <td colSpan={3}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          {brokenReport.length > 0 && (
            <div className="grid gap-1.5">
              <p className="text-xs font-bold uppercase tracking-wide text-rose-700">Broken Promises — for management review</p>
              <div className="overflow-x-auto rounded-md border border-rose-200">
                <table className="w-full text-xs" data-testid="broken-promise-table">
                  <thead className="bg-rose-50">
                    <tr className="[&>th]:whitespace-nowrap [&>th]:px-2.5 [&>th]:py-2 [&>th]:text-left [&>th]:font-semibold [&>th]:text-rose-900">
                      <th>Customer</th><th>Sub. No.</th><th>Promised On</th><th>Expected By</th><th className="text-right">Amount Pending</th><th>Overdue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {brokenReport.map((b) => (
                      <tr key={b.id} className="border-t [&>td]:px-2.5 [&>td]:py-2" data-testid="broken-promise-row">
                        <td className="font-medium">{b.customer}</td>
                        <td>{b.sub_no}</td>
                        <td className="whitespace-nowrap">{fmtDate(b.promised_on)}</td>
                        <td className="whitespace-nowrap">{b.expected_by ? fmtDate(b.expected_by) : "—"}</td>
                        <td className="text-right tabular-nums">{inr(b.amount)}</td>
                        <td><span className="rounded-full bg-rose-50 px-2 py-0.5 font-semibold text-rose-700">{b.days}d</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                const cell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
                const lines = [
                  `Call Outcome Report — ${monthLabel(reportMonth)}`,
                  "Customer,Calls,Outcomes,Last Call,Pending Amount",
                  ...outcomeReport.map((r) =>
                    [r.customer, r.calls, Object.entries(r.outcomes).map(([o, n]) => (n > 1 ? `${o} x${n}` : o)).join("; "), r.last_date, r.pending].map(cell).join(",")
                  ),
                  `"TOTAL",${reportTotalCalls},,,`,
                ].join("\n");
                const url = URL.createObjectURL(new Blob([lines], { type: "text/csv" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `call-outcome-report-${reportMonth}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              disabled={!outcomeReport.length}
              data-testid="call-report-excel-btn"
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" /> Excel
            </Button>
            <Button variant="outline" onClick={() => printCallOutcomeReport(outcomeReport, monthLabel(reportMonth), brokenReport)} disabled={!outcomeReport.length && !brokenReport.length} data-testid="call-report-print-btn">
              <Printer className="mr-2 h-4 w-4" /> Print
            </Button>
            <Button onClick={() => setReportOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={summaryOpen} onOpenChange={setSummaryOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[760px]" data-testid="collection-summary-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" /> Collection Summary
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap items-end gap-3">
            <div className="grid gap-1.5">
              <Label className="text-xs">Month</Label>
              <Input type="month" className="h-9 w-[170px]" value={summaryMonth} onChange={(e) => setSummaryMonth(e.target.value)} data-testid="collection-summary-month" />
            </div>
            <p className="pb-2 text-xs text-muted-foreground">Submissions dated in {monthLabel(summaryMonth)}</p>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-md border border-border p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Submitted</p>
              <p className="text-lg font-bold" data-testid="summary-submitted">{inr(summaryStats.total)}</p>
              <p className="text-[11px] text-muted-foreground">{summaryStats.count} slip(s)</p>
            </div>
            <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Cleared</p>
              <p className="text-lg font-bold text-emerald-700" data-testid="summary-cleared">{inr(summaryStats.cleared)}</p>
              <p className="text-[11px] text-emerald-700/80">{summaryStats.clearedCount} slip(s)</p>
            </div>
            <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">Pending</p>
              <p className="text-lg font-bold text-amber-700" data-testid="summary-pending">{inr(summaryStats.pending)}</p>
              <p className="text-[11px] text-amber-700/80">{summaryStats.pendingCount} slip(s){summaryStats.returnedCount ? ` · ${summaryStats.returnedCount} returned` : ""}</p>
            </div>
            <div className="rounded-md border border-primary/30 bg-primary/5 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">Collection %</p>
              <p className="text-lg font-bold text-primary" data-testid="summary-percent">
                {summaryStats.total > 0 ? Math.round((summaryStats.cleared / summaryStats.total) * 100) : 0}%
              </p>
              <p className="text-[11px] text-muted-foreground">of submitted value</p>
            </div>
          </div>
          {summaryCustRows.length > 0 && (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-xs" data-testid="collection-summary-table">
                <thead className="bg-slate-50">
                  <tr className="[&>th]:whitespace-nowrap [&>th]:px-2.5 [&>th]:py-2 [&>th]:text-left [&>th]:font-semibold [&>th]:text-slate-600">
                    <th>Customer</th><th className="text-right">Submissions</th><th className="text-right">Submitted</th><th className="text-right">Cleared</th><th className="text-right">Pending</th><th className="text-right">% Cleared</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryCustRows.map((r) => (
                    <tr key={r.customer} className="border-t [&>td]:px-2.5 [&>td]:py-2" data-testid="collection-summary-row">
                      <td className="font-medium">{r.customer}</td>
                      <td className="text-right">{r.subs}</td>
                      <td className="text-right tabular-nums">{inr(r.total)}</td>
                      <td className="text-right tabular-nums text-emerald-700">{inr(r.cleared)}</td>
                      <td className="text-right font-semibold tabular-nums text-amber-700">{inr(r.pending)}</td>
                      <td className="text-right">{r.total > 0 ? Math.round((r.cleared / r.total) * 100) : 0}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => printCollectionSummary(summaryStats, summaryCustRows, monthLabel(summaryMonth))} disabled={!summarySubs.length} data-testid="collection-summary-print-btn">
              <Printer className="mr-2 h-4 w-4" /> Print
            </Button>
            <Button onClick={() => setSummaryOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
